// Copyright ©️ 2026 eVolve MEP, LLC

import './creatable.css';

import React, { useCallback, useMemo, useState } from 'react';

import { Text, Flex, type Primitive } from '@mantine/core';

import { optionExists } from 'components/Mantine/TypeSafeSelect/common';
import type { CreatableModalProps } from 'components/Mantine/TypeSafeSelect/Creatables/CreatableModal';
import { CreatedBadge } from 'components/Mantine/TypeSafeSelect/Creatables/CreatedBadge';
import type { CreatableProps } from 'components/Mantine/TypeSafeSelect/Creatables/types';
import type {
  TypeSafeData,
  WrappedMultiSelectProps,
  WrappedSelectProps,
} from 'components/Mantine/TypeSafeSelect/types';
import type { WithRequired, WithRequiredIsh } from 'helpers/helper-types';
import { isNotNil } from 'helpers/isNotNil';

type OverrideKeys<T extends Primitive, D extends object> = Extract<
  keyof (WrappedSelectProps<T, D> & WrappedMultiSelectProps<T, D>),
  'data' | 'searchable' | 'onOptionSubmit' | 'renderOption'
>;

type Props<T extends Primitive, D extends object> = Pick<
  WrappedSelectProps<T, D> & WrappedMultiSelectProps<T, D>,
  keyof CreatableProps | OverrideKeys<T, D>
>;

/** Used to denote the `value` field of the "Create new value" option in a dropdown. */
export const creatorKey = '_$creatorKey' as const;
/** Used to denote the `value` field of the "created" value option in a dropdown. */
export const createdKey = '_$createdKey' as const;

/** Returned by this hook to assist with the creation of a new value. */
type CreatingProps = {
  /** The props to pass to the creation modal. */
  creationModalProps: CreatableModalProps;
};

/**
 * Used for "Creatables"
 * That is, Selects that allow the user to create a new value instead of selecting an existing option.
 */
export const useCreatable = <T extends Primitive, D extends object>(
  {
    __searchValue,
    __onSearchChange,
  }: {
    __searchValue: string;
    __onSearchChange: (searchValue: string) => void;
  },
  {
    creatable,
    createdValue,
    onCreate,
    createLabel = 'Create new value',
    searchable,
    data,
    onOptionSubmit: onOptionSubmitParent,
    renderOption: renderOptionParent,
    creationModal,
    modalBehavior = 'only-if-empty',
  }: Props<T, D>,
): WithRequired<Pick<Props<T, D>, OverrideKeys<T, D>>> &
  WithRequiredIsh<Pick<Props<T, D>, keyof CreatableProps>> &
  CreatingProps => {
  const createValue = useMemo(() => __searchValue.trim(), [__searchValue]);
  const createValueExists = useMemo(() => optionExists(data, createValue), [data, createValue]);

  const dataWithOption = useMemo<TypeSafeData<T, D>>(
    () =>
      [
        ...data,
        ...(createdValue
          ? [
              {
                value: createdKey,
                label: createdValue,
              },
            ]
          : []),
        ...(creatable
          ? [
              {
                value: creatorKey,
                label: createValueExists ? '' : createValue,
              },
            ]
          : []),
      ] as TypeSafeData<T, D>,
    [data, createdValue, creatable, createValueExists, createValue],
  );

  // The default value to show in the `CreatableModal`
  const [defaultValue, setDefaultValue] = useState<string | null>(null);

  const usingCreationModal = useMemo(() => !!creationModal, [creationModal]);

  const onOptionSubmit = useCallback<NonNullable<typeof onOptionSubmitParent>>(
    (v) => {
      if (v === createdKey) return;

      if (creatable && v === creatorKey) {
        if (
          !usingCreationModal &&
          (modalBehavior === 'no-modal' ||
            (modalBehavior === 'only-if-empty' && createValue.length > 0 && !createValueExists))
        ) {
          setTimeout(() => {
            onCreate?.(createValue);
          });
        } else {
          setDefaultValue(!createValueExists ? createValue : '');
        }
        __onSearchChange('');
      } else {
        onOptionSubmitParent?.(v);
      }
    },
    [
      creatable,
      usingCreationModal,
      modalBehavior,
      createValue,
      createValueExists,
      __onSearchChange,
      onCreate,
      onOptionSubmitParent,
    ],
  );

  const renderOption = useCallback<NonNullable<typeof renderOptionParent>>(
    (opts) => {
      if (creatable) {
        if (opts.option.value === creatorKey) {
          return (
            <Flex align="center" gap={4} style={{ textWrap: 'wrap' }}>
              <Text fw={500} className="creatable-option" lineClamp={3}>
                + {createLabel}
                {!!opts.option.label && ': '}
                <Text span className="creatable-option-text">
                  {opts.option.label}
                </Text>
              </Text>
            </Flex>
          );
        }
        if (opts.option.value === createdKey) {
          return (
            <Flex gap={4} align="center" style={{ textWrap: 'wrap' }}>
              <Text lineClamp={3}>{opts.option.label}</Text>
              <CreatedBadge />
            </Flex>
          );
        }
      }

      return renderOptionParent?.(opts);
    },
    [creatable, createLabel, renderOptionParent],
  );

  const creatingProps = useMemo<CreatingProps>(
    () => ({
      creationModalProps: {
        opened: isNotNil(defaultValue),
        onClose: () => setDefaultValue(null),
        title: createLabel,
        onSubmit: (value) => onCreate?.(value),
        defaultValue: defaultValue ?? '',
        creationModal,
      },
    }),
    [createLabel, creationModal, defaultValue, onCreate],
  );

  return useMemo(
    () => ({
      data: dataWithOption,
      searchable: (searchable || creatable) ?? false,
      renderOption,
      onOptionSubmit,
      ...creatingProps,

      // Returned so they can be programmatically excluded from the spread props onto the input
      creatable,
      createLabel,
      createdValue,
      onCreate,
      creationModal,
      modalBehavior,
    }),
    [
      dataWithOption,
      searchable,
      creatable,
      renderOption,
      onOptionSubmit,
      creatingProps,
      createLabel,
      createdValue,
      onCreate,
      creationModal,
      modalBehavior,
    ],
  );
};
